import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCreditFacilityListQueryKey,
  useCreditFacilityUpsert,
} from "gen/endpoints/credit-facility/credit-facility";
import { useLenderList } from "gen/endpoints/lender/lender";
import { CreditFacilityData } from "gen/model/creditFacilityData";
import { CreditFacilityTypeEnum } from "gen/model/creditFacilityTypeEnum";
import { CreditFacilityUpsertDataRequest } from "gen/model/creditFacilityUpsertDataRequest";
import { creditFacilityUpsertBody } from "gen/schemas/credit-facility/credit-facility";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { useBoolean } from "usehooks-ts";
import { z as zod } from "zod";

export interface CreditFacilitySetupModalProps {
  fundFamilyId: string;
  onCloseComplete: () => void;
}

export const CreditFacilityCreateModal: React.FC<
  CreditFacilitySetupModalProps
> = ({ fundFamilyId, onCloseComplete }) => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync: createFacility } = useCreditFacilityUpsert();
  const { data: lenderEntitiesData } = useLenderList();

  const { value: isOpen, setFalse: close } = useBoolean(true);
  const form = useForm<CreditFacilityUpsertDataRequest>({
    resolver: zodResolver(
      creditFacilityUpsertBody.superRefine((values, ctx) => {
        if (new Date(values.inception_date) >= new Date(values.maturity_date)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.invalid_date,
            message: t("creditFacility:create.maturityDateError"),
            path: ["maturity_date"],
          });
        }
      }),
    ),
  });
  const onSubmit = useCallback(
    async (data: CreditFacilityUpsertDataRequest) => {
      const newData = await createFacility({ fundFamilyId, data });
      await queryClient.invalidateQueries({
        queryKey: getCreditFacilityListQueryKey(fundFamilyId),
      });
      return newData;
    },
    [createFacility, fundFamilyId, queryClient],
  );

  const onSuccess = useCallback(
    (newData: CreditFacilityData) => {
      navigate(
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .creditFacilities({})
          .creditFacility({ creditFacilityId: newData.id })
          .setup({}).$,
      );
      close();
    },
    [close, navigate, fundFamilyId],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={onSuccess}
        successMessage={t("creditFacility:create.success")}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("creditFacility:create.title")}</ModalHeader>
          <ModalBody>
            <FormHiddenInput name="fund_family_id" value={fundFamilyId} />
            <FormTextInput
              name="name"
              title={t("creditFacility:create.creditFacilityName")}
            />
            <FormSelect
              name="facility_type"
              title={t("creditFacility:create.facilityType")}
            >
              <SelectOption value={CreditFacilityTypeEnum.commitment_backed}>
                {t("enums:CreditFacilityTypeEnum.commitment_backed")}
              </SelectOption>
            </FormSelect>
            <FormSelect
              name="lead_bank_id"
              title={t("creditFacility:create.leadBank")}
            >
              {lenderEntitiesData?.map(({ id, legal_name }) => (
                <SelectOption key={`lender_${id}`} value={id!.toString()}>
                  {legal_name}
                </SelectOption>
              ))}
            </FormSelect>
            <FormDateInput
              name="inception_date"
              title={t("creditFacility:create.inceptionDate")}
            />
            <FormDateInput
              name="maturity_date"
              title={t("creditFacility:create.maturityDate")}
            />
            <FormCheckbox name="transaction_code_configs_available">
              <Text>
                {t("creditFacility:create.transactionCodeConfigsAvailable")}
                <Tooltip
                  key="tooltip"
                  label={t(
                    "creditFacility:create.transactionCodeConfigsAvailableTooltip",
                  )}
                  placement="top"
                >
                  <InfoCircleIcon
                    w="12px"
                    h="12px"
                    ml="10px"
                    color="gray.400"
                  />
                </Tooltip>
              </Text>
            </FormCheckbox>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} />}
              confirm={
                <FormSubmitButton>
                  {t("creditFacility:create.submit")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
